/**
 * Subscription History Page
 * View subscription audit logs and history
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import SubscriptionLayout from '../../components/subscription/SubscriptionLayout';
import { useAuth } from '../../contexts/AuthContext';
import { Clock } from 'lucide-react';
import { getSubscriptionAuditLogs, SubscriptionAuditEvent } from '../../services/SubscriptionService';

const HistoryPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [auditLogs, setAuditLogs] = useState<SubscriptionAuditEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const organizationId = searchParams.get('org') || user?.organizationId || null;

  useEffect(() => {
    if (organizationId) {
      loadHistory();
    }
  }, [organizationId]);

  const loadHistory = async () => {
    if (!organizationId) return;
    setIsLoading(true);
    try {
      const logs = await getSubscriptionAuditLogs(organizationId, 100);
      setAuditLogs(logs);
    } catch (error) {
      console.error('Failed to load subscription history:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getEventDescription = (log: SubscriptionAuditEvent) => {
    switch (log.eventType) {
      case 'subscription_created':
        return `Subscription created (${log.details.newPlanType || 'N/A'})`;
      case 'subscription_status_changed':
        if (log.details.previousStatus && log.details.newStatus) {
          return `Status: ${log.details.previousStatus} → ${log.details.newStatus}${log.details.newPlanType ? ` (${log.details.newPlanType})` : ''}`;
        }
        return `Status changed to ${log.details.newStatus || 'N/A'}`;
      case 'subscription_plan_changed':
        if (log.details.previousPlanType && log.details.newPlanType) {
          return `Plan changed: ${log.details.previousPlanType} → ${log.details.newPlanType}${log.details.newStatus ? ` (Status: ${log.details.newStatus})` : ''}`;
        }
        return `Plan changed to ${log.details.newPlanType || 'N/A'}`;
      case 'payment_received':
        const amount = log.details.metadata?.amount;
        const currency = log.details.metadata?.currency;
        return amount ? `Payment received: ${currency ? currency.toUpperCase() : '$'}${amount.toFixed(2)}` : 'Payment received';
      case 'payment_failed':
        return 'Payment failed';
      case 'subscription_cancelled':
        return 'Subscription cancelled';
      case 'trial_expired':
        return 'Trial expired';
      case 'stripe_info_updated':
        return 'Stripe info updated';
      default:
        return (log.eventType as string).replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
    }
  };

  return (
    <SubscriptionLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center space-x-2">
            <Clock className="w-8 h-8 text-purple-600 dark:text-purple-400" />
            <span>Subscription History</span>
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            View complete history of subscription changes and events
          </p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center p-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          </div>
        ) : auditLogs.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center border border-gray-100 dark:border-gray-700">
            <Clock className="w-16 h-16 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">No history available yet</p>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 border border-gray-100 dark:border-gray-700">
            <div className="space-y-3">
              {auditLogs.map((log) => {
                const logDate = new Date(log.timestamp);
                const isValidDate = !isNaN(logDate.getTime());

                return (
                  <div key={log.eventId} className="flex items-start justify-between p-4 border border-gray-200 dark:border-slate-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 dark:text-white">
                        {getEventDescription(log)}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {isValidDate ? logDate.toLocaleString() : 'N/A'}
                      </p>
                      {log.details.reason && (
                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                          {log.details.reason}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <span
                        className={`inline-block px-2 py-1 rounded text-xs ${
                          log.eventType === 'payment_received' || log.eventType === 'subscription_created'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
                            : log.eventType === 'payment_failed' || log.eventType === 'trial_expired'
                              ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300'
                              : 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300'
                        }`}
                      >
                        {log.details.triggeredBy || 'system'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </SubscriptionLayout>
  );
};

export default HistoryPage;


